import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { getMonthStem, getTimeStem, getYearsWithPillar } from '../../../utils/lunarCalc';

// ─── Constants ────────────────────────────────────────────────────────────────

const STEMS   = ['甲','乙','丙','丁','戊','己','庚','辛','壬','癸'];
const BRANCHES = ['子','丑','寅','卯','辰','巳','午','未','申','酉','戌','亥'];

// 五行 colors (wood/fire/earth/metal/water)
const STEM_COLORS: string[] = [
  '#4ade80','#22c55e', // 甲乙 Wood
  '#f87171','#ef4444', // 丙丁 Fire
  '#fbbf24','#f59e0b', // 戊己 Earth
  '#94a3b8','#cbd5e1', // 庚辛 Metal
  '#60a5fa','#3b82f6', // 壬癸 Water
];
const BRANCH_COLORS: string[] = [
  '#60a5fa', // 子 Water
  '#fbbf24', // 丑 Earth
  '#4ade80', // 寅 Wood
  '#22c55e', // 卯 Wood
  '#fbbf24', // 辰 Earth
  '#f87171', // 巳 Fire
  '#ef4444', // 午 Fire
  '#f59e0b', // 未 Earth
  '#94a3b8', // 申 Metal
  '#cbd5e1', // 酉 Metal
  '#fbbf24', // 戌 Earth
  '#3b82f6', // 亥 Water
];

// Month branches for months 1-12: 寅卯辰巳午未申酉戌亥子丑
const MONTH_BRANCHES = [2,3,4,5,6,7,8,9,10,11,0,1];

export interface FourPillarsValue {
  yearStem:   number; // 0-9, -1 unset
  yearBranch: number; // 0-11, -1 unset
  monthBranch: number; // 0-11 (index into BRANCHES)
  dayStem:    number; // 0-9, -1 unset
  dayBranch:  number; // 0-11, -1 unset
  timeBranch: number; // 0-11
}

interface FourPillarsTabProps {
  initial: FourPillarsValue;
  onChange: (v: FourPillarsValue) => void;
}

type PillarKey = 'year' | 'month' | 'day' | 'time';
type EditPart  = 'stem' | 'branch';

// ─── Component ────────────────────────────────────────────────────────────────

export default function FourPillarsTab({ initial, onChange }: FourPillarsTabProps) {
  const { t } = useTranslation('profileManage');

  const [yStem,   setYStem]   = useState(initial.yearStem);
  const [yBranch, setYBranch] = useState(initial.yearBranch);
  const [mBranch, setMBranch] = useState(initial.monthBranch);
  const [dStem,   setDStem]   = useState(initial.dayStem);
  const [dBranch, setDBranch] = useState(initial.dayBranch);
  const [tBranch, setTBranch] = useState(initial.timeBranch);

  // Derived (auto)
  const mStem = yStem >= 0 ? getMonthStem(yStem, mBranch) : -1;
  const tStem = dStem >= 0 ? getTimeStem(dStem, tBranch)  : -1;

  // Year range
  const yearRange = (yStem >= 0 && yBranch >= 0)
    ? getYearsWithPillar(yStem, yBranch)
    : [];

  // Active pillar + edit part
  const [activePillar, setActivePillar] = useState<PillarKey>('year');
  const [activeEdit,   setActiveEdit]   = useState<EditPart>('stem');

  // Notify parent on any change
  useEffect(() => {
    onChange({ yearStem: yStem, yearBranch: yBranch, monthBranch: mBranch, dayStem: dStem, dayBranch: dBranch, timeBranch: tBranch });
  }, [yStem, yBranch, mBranch, dStem, dBranch, tBranch]);

  // ── Handlers ────────────────────────────────────────────────────────────────

  const handleStemClick = (idx: number) => {
    if (activePillar === 'year') {
      if (yBranch >= 0 && yBranch % 2 !== idx % 2) setYBranch(-1);
      setYStem(idx);
      // Auto-advance to branch
      setActiveEdit('branch');
    } else if (activePillar === 'day') {
      if (dBranch >= 0 && dBranch % 2 !== idx % 2) setDBranch(-1);
      setDStem(idx);
      setActiveEdit('branch');
    }
    // month/time stems are auto — clicking does nothing
  };

  const handleBranchClick = (idx: number) => {
    if (activePillar === 'year') {
      if (yStem >= 0 && yStem % 2 !== idx % 2) return; // parity mismatch — ignore
      setYBranch(idx);
      if (yStem >= 0) {
        setActivePillar('month');
        setActiveEdit('branch');
      } else {
        setActiveEdit('stem');
      }
    } else if (activePillar === 'month') {
      setMBranch(idx);
      setActivePillar('day');
      setActiveEdit('stem');
    } else if (activePillar === 'day') {
      if (dStem >= 0 && dStem % 2 !== idx % 2) return;
      setDBranch(idx);
      if (dStem >= 0) {
        setActivePillar('time');
        setActiveEdit('branch');
      } else {
        setActiveEdit('stem');
      }
    } else if (activePillar === 'time') {
      setTBranch(idx);
    }
  };

  const handleClear = () => {
    setYStem(-1); setYBranch(-1);
    setMBranch(MONTH_BRANCHES[0]);
    setDStem(-1); setDBranch(-1);
    setTBranch(0);
    setActivePillar('year');
    setActiveEdit('stem');
  };

  // ── Availability checks ──────────────────────────────────────────────────────

  const isStemClickable = (idx: number): boolean => {
    if (activePillar === 'month' || activePillar === 'time') return false;
    if (activeEdit === 'branch') return false;
    if (activePillar === 'year'  && yBranch >= 0) return idx % 2 === yBranch % 2;
    if (activePillar === 'day'   && dBranch >= 0) return idx % 2 === dBranch % 2;
    return true;
  };

  const isBranchClickable = (idx: number): boolean => {
    if (activePillar === 'year' || activePillar === 'day') {
      if (activeEdit === 'stem') return false;
      const currentStem = activePillar === 'year' ? yStem : dStem;
      if (currentStem >= 0) return idx % 2 === currentStem % 2;
      return true;
    }
    return true; // month / time: all branches selectable
  };

  // ── Pillar circle ────────────────────────────────────────────────────────────

  interface PillarCircleProps {
    charIdx: number;
    colors: string[];
    chars: string[];
    isAuto?: boolean;
    isActive: boolean;
    onClick: () => void;
  }

  function PillarCircle({ charIdx, colors, chars, isAuto, isActive, onClick }: PillarCircleProps) {
    const color  = charIdx >= 0 ? colors[charIdx] : '#6B5576';
    const char   = charIdx >= 0 ? chars[charIdx] : '?';
    const bgColor = charIdx >= 0 ? `${color}22` : 'rgba(107,85,118,0.12)';

    return (
      <button
        onClick={onClick}
        className="relative w-9 h-9 rounded-full flex items-center justify-center transition-all"
        style={{
          background: bgColor,
          border: isActive
            ? `2px solid ${color}`
            : '2px solid rgba(183,154,197,0.2)',
          boxShadow: isActive ? `0 0 8px ${color}55` : 'none',
        }}
      >
        <span
          style={{
            fontFamily: "'Noto Serif TC', serif",
            fontSize: '1rem',
            fontWeight: charIdx >= 0 ? '700' : '400',
            color: charIdx >= 0 ? color : '#6B5576',
          }}
        >
          {char}
        </span>
        {isAuto && charIdx >= 0 && (
          <span
            className="absolute -top-1 -right-1 text-[0.45rem] px-0.5 rounded-full"
            style={{ background: 'rgba(107,85,118,0.8)', color: '#B79AC5' }}
          >
            {t('birthTimePicker.autoLabel')}
          </span>
        )}
      </button>
    );
  }

  // ── Pillar column ────────────────────────────────────────────────────────────

  interface PillarColumnProps {
    label: string;
    stemIdx: number;
    branchIdx: number;
    stemIsAuto?: boolean;
    pillarKey: PillarKey;
  }

  function PillarColumn({ label, stemIdx, branchIdx, stemIsAuto, pillarKey }: PillarColumnProps) {
    const stemActive   = activePillar === pillarKey && activeEdit === 'stem';
    const branchActive = activePillar === pillarKey && activeEdit === 'branch';

    return (
      <div className="flex flex-col items-center gap-1.5">
        <span
          className="text-[0.6rem]"
          style={{ color: '#8A7B91', fontFamily: "'Noto Serif TC', serif" }}
        >
          {label}
        </span>
        <PillarCircle
          charIdx={stemIdx}
          colors={STEM_COLORS}
          chars={STEMS}
          isAuto={stemIsAuto}
          isActive={stemActive}
          onClick={() => {
            setActivePillar(pillarKey);
            if (!stemIsAuto) setActiveEdit('stem');
          }}
        />
        <PillarCircle
          charIdx={branchIdx}
          colors={BRANCH_COLORS}
          chars={BRANCHES}
          isActive={branchActive}
          onClick={() => {
            setActivePillar(pillarKey);
            setActiveEdit('branch');
          }}
        />
      </div>
    );
  }

  // ── Character button ─────────────────────────────────────────────────────────

  interface CharBtnProps {
    char: string;
    color: string;
    clickable: boolean;
    isSelected: boolean;
    onClick: () => void;
  }

  function CharBtn({ char, color, clickable, isSelected, onClick }: CharBtnProps) {
    return (
      <button
        onClick={onClick}
        disabled={!clickable}
        className="h-9 rounded-xl flex items-center justify-center transition-all"
        style={{
          background: isSelected
            ? `${color}33`
            : clickable ? 'rgba(107,78,113,0.18)' : 'rgba(107,78,113,0.06)',
          border: isSelected
            ? `1.5px solid ${color}`
            : '1.5px solid transparent',
          color: clickable ? color : `${color}40`,
          fontFamily: "'Noto Serif TC', serif",
          fontSize: '1rem',
          fontWeight: isSelected ? '700' : '500',
          boxShadow: isSelected ? `0 0 6px ${color}44` : 'none',
        }}
      >
        {char}
      </button>
    );
  }

  // ── Selected stem/branch for current pillar ─────────────────────────────────
  const activeStem   = activePillar === 'year' ? yStem   : activePillar === 'day' ? dStem   : -1;
  const activeBranch = activePillar === 'year' ? yBranch : activePillar === 'month' ? mBranch : activePillar === 'day' ? dBranch : tBranch;

  // ── Render ───────────────────────────────────────────────────────────────────

  return (
    <div className="px-3 py-1">
      {/* 4 Pillars display */}
      <div className="grid grid-cols-4 gap-1 mb-3">
        <PillarColumn
          label={t('birthTimePicker.yearPillar')}
          stemIdx={yStem}
          branchIdx={yBranch}
          pillarKey="year"
        />
        <PillarColumn
          label={t('birthTimePicker.monthPillar')}
          stemIdx={mStem}
          branchIdx={mBranch}
          stemIsAuto
          pillarKey="month"
        />
        <PillarColumn
          label={t('birthTimePicker.dayPillar')}
          stemIdx={dStem}
          branchIdx={dBranch}
          pillarKey="day"
        />
        <PillarColumn
          label={t('birthTimePicker.hourPillar')}
          stemIdx={tStem}
          branchIdx={tBranch}
          stemIsAuto
          pillarKey="time"
        />
      </div>

      {/* Year range + clear */}
      <div className="flex items-center justify-between mb-2">
        <div className="text-[0.6rem]" style={{ color: '#8A7B91' }}>
          {yearRange.length > 0 ? (
            <>
              <span style={{ color: '#B79AC5' }}>{t('birthTimePicker.searchRange')}：</span>
              {yearRange.slice(0, 4).join('、')}
              {yearRange.length > 4 && '...'}
              <span style={{ color: '#B79AC5' }}>{t('birthTimePicker.year')}</span>
            </>
          ) : (
            <span style={{ color: '#6B5576' }}>{t('birthTimePicker.pillarHint')}</span>
          )}
        </div>
        <button
          onClick={handleClear}
          className="flex items-center gap-0.5 text-[0.6rem] px-2 py-0.5 rounded-full"
          style={{
            background: 'rgba(107,78,113,0.18)',
            color: '#8A7B91',
            border: '1px solid rgba(183,154,197,0.2)',
          }}
        >
          <i className="ri-delete-bin-line text-[0.65rem]" />
          {t('birthTimePicker.clearBtn')}
        </button>
      </div>

      <div className="h-px mb-2" style={{ background: 'rgba(183,154,197,0.15)' }} />

      {/* Heavenly Stems grid */}
      <div className="grid grid-cols-5 gap-1 mb-1.5">
        {STEMS.map((s, idx) => (
          <CharBtn
            key={idx}
            char={s}
            color={STEM_COLORS[idx]}
            clickable={isStemClickable(idx)}
            isSelected={activeEdit === 'stem' && activeStem === idx}
            onClick={() => handleStemClick(idx)}
          />
        ))}
      </div>

      {/* Earthly Branches grid */}
      <div className="grid grid-cols-6 gap-1">
        {BRANCHES.map((b, idx) => (
          <CharBtn
            key={idx}
            char={b}
            color={BRANCH_COLORS[idx]}
            clickable={isBranchClickable(idx)}
            isSelected={activeEdit === 'branch' && activeBranch === idx}
            onClick={() => handleBranchClick(idx)}
          />
        ))}
      </div>
    </div>
  );
}
